import { Injectable } from '@angular/core';
import { NotificationTemplateService as BaseNotificationTemplateService } from '@shared/modules/notification/services/notification-template.service';
import { NotificationTemplate } from '@shared/modules/notification/interfaces/notification-template';
import { Notification, NotificationType } from '@core/models/notification';
import { TeamUserRoleLocalization } from '@core/localization/team-user-role';
import { TeamUserRole } from '@core/models/team-user';
import { TeamEventTypes } from '@core/models/team-event-types';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import * as RoutingMap from '@app/routing-map';
import { TeamService } from '@core/services/team.service';
import { UserService } from '@core/services/user.service';
import { Router } from '@angular/router';
import { TeamEventService } from '@core/services/team-event.service';
import { NotificationService } from '@core/services/notification.service';
import { LocalizationService } from '@shared/modules/localization/localization.service';
import notificationLocalization from '@app/notification.localization';
import { replaceParams } from '@shared/modules/localization/utils';

@Injectable()
export class NotificationTemplateService extends BaseNotificationTemplateService {
  private localization = notificationLocalization[this.localizationService.getLocale()];

  protected data: {[key: number]: NotificationTemplate} = {
    [NotificationType.team_updated]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => 'name' in (item.data.diff || {})
        ? replaceParams(this.localization.teamNameChanged, {old: item.data.diff.name.old, new: item.data.diff.name.new})
        : 'logo' in (item.data.diff || {})
          ? replaceParams(this.localization.teamLogoChanged, {teamName: item.team.name})
          : this.localization.teamDataChanged,
      description: () => '',
      route: (item) => this.getTeamRoute(item)
    },
    [NotificationType.team_invited]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => this.localization.teamInvited,
      description: (item: Notification) => replaceParams(
        this.localization.teamInvitedDesc,
        {
          initiator: item.initiator.shortName,
          teamName: item.team.name
        }
      ),
      actions: [
        {
          text: this.localization.declineInvite,
          action: item => this.declineTeamInvite(item),
          buttonColor: 'default',
          buttonType: 'link',
          markViewed: true,
        },
        {
          text: this.localization.acceptInvite,
          action: item => this.acceptTeamInvite(item),
          buttonColor: 'primary',
          buttonType: 'button',
          markViewed: true,
        },
      ],
      route: (item) => this.getTeamRoute(item)
    },
    [NotificationType.team_join_request]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(this.localization.teamJoinRequest, {teamName: item.team.name}),
      description: (item: Notification) => replaceParams(
        this.localization.joinRequestDesc,
        { initiator: item.initiator.shortName, teamName: item.team.name }
      ),
      actions: [
        {
          text: this.localization.joinRequestDecline,
          action: item => this.declineTeamJoinRequest(item),
          buttonColor: 'default',
          buttonType: 'link',
          markViewed: true,
        },
        {
          text: this.localization.joinRequestAccept,
          action: item => this.acceptTeamJoinRequest(item),
          buttonColor: 'primary',
          buttonType: 'button',
          markViewed: true,
        },
      ],
      route: (item) => this.getUserRoute(item)
    },
    [NotificationType.team_join_declined_by_team]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(this.localization.teamJoinDeclinedByTeam, { teamName: item.team.name }),
      description: () => '',
      route: (item) => this.getTeamRoute(item)
    },
    [NotificationType.team_join_declined_by_user]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.teamJoinDeclinedByUser,
        { userName: item.teamInvite.user.shortName, teamName: item.team.name }
      ),
      description: () => '',
      route: (item) => this.getUserRoute(item)
    },
    [NotificationType.team_join_canceled_by_team]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(this.localization.teamJoinCanceledByTeam, { teamName: item.team.name }),
      description: () => '',
      route: item => this.getTeamRoute(item)
    },
    [NotificationType.team_join_canceled_by_user]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.teamJoinCanceledByUser,
        { initiator: item.initiator.shortName, teamName: item.team.name }
      ),
      description: () => '',
      route: item => this.getUserRoute(item)
    },
    [NotificationType.team_joined_for_team]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(this.localization.teamJoinedForTeam, { teamName: item.team.name }),
      description: (item: Notification) => replaceParams(
        this.localization.teamJoinedForTeamDesc,
        { teamName: item.team.name, userName: item.teamUser.user.shortName }
      ),
      route: item => this.getUserRoute(item)
    },
    [NotificationType.team_joined_for_user]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(this.localization.teamJoinedForUser, { teamName: item.team.name }),
      description: (item: Notification) => replaceParams(
        this.localization.teamJoinedForUserDesc,
        { teamName: item.team.name }
      ),
      route: item => this.getTeamRoute(item)
    },
    [NotificationType.team_user_role_changed_for_team]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.roleChangedForTeam,
        { userName: item.teamUser.user.shortName, teamName: item.team.name, role: TeamUserRoleLocalization[this.localizationService.getLocale()][item.data.role] }
      ),
      description: (item: Notification) => '',
      route: item => this.getTeamRoute(item)
    },
    [NotificationType.team_user_role_changed_for_user]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => this.localization.roleChangedForUser,
      description: (item: Notification) => replaceParams(
        this.localization.roleChangedForUserDesc,
        { initiator: item.initiator.shortName, teamName: item.team.name, role: TeamUserRoleLocalization[this.localizationService.getLocale()][item.data.role] }
      ),
      route: item => this.getTeamRoute(item)
    },
    [NotificationType.team_user_number_changed]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.numberChanged,
        { teamName: item.team.name, number: item.data.number }
      ),
      description: () => '',
      route: item => this.getTeamRoute(item)
    },
    [NotificationType.team_user_deleted_for_team]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.teamUserDeletedForTeam,
        { userName: item.teamUser.user.shortName, teamName: item.team.name }
      ),
      description: (item: Notification) => '',
      route: item => this.getUserRoute(item)
    },
    [NotificationType.team_user_deleted_for_user]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => this.localization.teamUserDeletedForUser,
      description: (item: Notification) => replaceParams(
        this.localization.teamUserDeletedForUserDesc,
        { teamName: item.team.name}
      ),
      route: item => this.getTeamRoute(item)
    },
    [NotificationType.team_user_left]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.teamUserLeft,
        { userName: item.teamUser.user.shortName }
      ),
      description: (item: Notification) => replaceParams(
        this.localization.teamUserLeftDesc,
        { userName: item.teamUser.user.shortName, teamName: item.team.name }
      ),
      route: item => this.getUserRoute(item)
    },

    [NotificationType.game_updated]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.gameUpdated,
        { competitor: this.getGameCompetitorTeamName(item)}
      ),
      description: (item: Notification) => replaceParams(
        this.localization.gameUpdatedDesc,
        {
          date: item.game.datetime && item.game.datetime.format('DD.MM') || '',
          time: item.game.datetime && item.game.datetime.format('HH:mm') || '',
          location: item.game.location,
        }
      ),
      route: item => this.getGameRoute(item)
    },
    [NotificationType.game_invited]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.gameInvited,
        { teamName: item.team.name, competitor: this.getGameCompetitorTeamName(item) }
      ),
      description: (item: Notification) => replaceParams(
        this.localization.gameInvitedDesc,
        {
          date: item.game.datetime && item.game.datetime.format('DD.MM') || '',
          time: item.game.datetime && item.game.datetime.format('HH:mm') || '',
          location: item.game.location,
        }
      ),
      actions: [
        {
          text: this.localization.gameInvitedDecline,
          action: item => this.declineGameInvite(item),
          buttonColor: 'default',
          buttonType: 'link',
          markViewed: true,
        },
        {
          text: this.localization.gameInvitedAccept,
          action: item => this.acceptGameInvite(item),
          buttonColor: 'primary',
          buttonType: 'button',
          markViewed: true,
        },
      ],
      route: item => this.getGameRoute(item)
    },
    [NotificationType.game_invite_accepted]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.gameInviteAccepted,
        { userName: item.gameInvite.teamUser.user.shortName, competitor: this.getGameCompetitorTeamName(item) }
      ),
      description: () => '',
      route: item => this.getGameRoute(item)
    },
    [NotificationType.game_invite_declined]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.gameInviteDeclined,
        { userName: item.gameInvite.teamUser.user.shortName, competitor: this.getGameCompetitorTeamName(item) }
      ),
      description: () => '',
      route: item => this.getGameRoute(item)
    },
    [NotificationType.game_remind]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.gameRemind,
        { competitor: this.getGameCompetitorTeamName(item) }
      ),
      description: (item: Notification) => replaceParams(
        this.localization.gameRemindDesc,
        {
          date: item.game.datetime && item.game.datetime.format('DD.MM') || '',
          time: item.game.datetime && item.game.datetime.format('HH:mm') || '',
          location: item.game.location,
        }
      ),
      route: item => this.getGameRoute(item)
    },
    [NotificationType.game_won]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.gameWon,
        { competitor: this.getGameCompetitorTeamName(item) }
      ),
      description: (item: Notification) => this.localization.gameWonDesc,
      route: item => this.getGameRoute(item)
    },
    [NotificationType.game_losing]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.gameLose,
        { competitor: this.getGameCompetitorTeamName(item) }
      ),
      description: (item: Notification) => this.localization.gameLoseDesc,
      route: item => this.getGameRoute(item)
    },
    [NotificationType.game_tech_victory]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.gameTechVictory,
        { competitor: this.getGameCompetitorTeamName(item) }
      ),
      description: (item: Notification) => replaceParams(
        this.localization.gameTechVictoryDesc,
        {
          competitor: this.getGameCompetitorTeamName(item),
          date: item.game.datetime && item.game.datetime.format('DD.MM') || '',
          time: item.game.datetime && item.game.datetime.format('HH:mm') || '',
        }
      ),
      route: item => this.getGameRoute(item)
    },
    [NotificationType.game_tech_defeat]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.gameTechDefeat,
        { competitor: this.getGameCompetitorTeamName(item) }
      ),
      description: () => ``,
      route: item => this.getGameRoute(item)
    },
    [NotificationType.poll_created]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.pollCreated,
        { teamName: item.team.name }
      ),
      description: () => this.localization.pollCreatedDesc,
      route: item => this.getPollRoute(item)
    },
    [NotificationType.poll_closed]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.pollClosed,
        { teamName: item.team.name }
      ),
      description: () => '',
      route: item => this.getPollRoute(item)
    },
    [NotificationType.team_event_invited]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => (
        item.teamEvent.eventType === TeamEventTypes.training
          ? replaceParams(this.localization.teamEventTrainingInvited, {teamName: item.team.name})
          : replaceParams(this.localization.teamEventCustomInvited, {teamName: item.team.name, eventName: item.teamEvent.eventCustomName})
      ),
      description: (item: Notification) => (
        item.teamEvent.eventType === TeamEventTypes.training
          ? replaceParams(this.localization.teamEventTrainingInvitedDesc, { date: item.teamEvent.datetime.format('DD.MM'), time: item.teamEvent.datetime.format('HH:mm'), location: item.teamEvent.location })
          : replaceParams(this.localization.teamEventCustomInvitedDesc, { eventName: item.teamEvent.eventCustomName, date: item.teamEvent.datetime.format('DD.MM'), time: item.teamEvent.datetime.format('HH:mm'), location: item.teamEvent.location })
      ),
      actions: [
        {
          text: this.localization.teamEventInviteDecline,
          action: item => this.declineTeamEventInvite(item),
          buttonColor: 'default',
          buttonType: 'link',
          markViewed: true,
        },
        {
          text: this.localization.teamEventInviteDecline,
          action: item => this.acceptTeamEventInvite(item),
          buttonColor: 'primary',
          buttonType: 'button',
          markViewed: true,
        },
      ],
      route: item => this.getTeamEventRoute(item)
    },
    [NotificationType.team_event_accepted]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => (
        item.teamEvent.eventType === TeamEventTypes.training
          ? replaceParams(this.localization.teamTrainingAccepted, { userName: item.teamUser.user.shortName, datetime: item.teamEvent.datetime.format('DD.MM, HH:mm')})
          : replaceParams(this.localization.teamEventAccepted, { userName: item.teamUser.user.shortName, eventName: item.teamEvent.eventCustomName})
      ),
      description: () => '',
      route: item => this.getTeamEventRoute(item)
    },
    [NotificationType.team_event_declined]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => item.teamEvent.eventType === TeamEventTypes.training
        ? replaceParams(this.localization.teamTrainingDeclined, { userName: item.teamUser.user.shortName, datetime: item.teamEvent.datetime.format('DD.MM, HH:mm')})
        : replaceParams(this.localization.teamEventDeclined, { userName: item.teamUser.user.shortName, eventName: item.teamEvent.eventCustomName}),
      description: () => '',
      route: item => this.getTeamEventRoute(item)
    },
    [NotificationType.team_event_remind]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => item.teamEvent.eventType === TeamEventTypes.training
        ? replaceParams(this.localization.teamTrainingRemind, { teamName: item.team.name })
        : replaceParams(this.localization.teamEventRemind, { teamName: item.team.name, eventName: item.teamEvent.eventCustomName }),
      description: (item: Notification) => item.teamEvent.eventType === TeamEventTypes.training
        ? replaceParams(this.localization.teamEventTrainingInvitedDesc, { date: item.teamEvent.datetime.format('DD.MM'), time: item.teamEvent.datetime.format('HH:mm'), location: item.teamEvent.location })
        : replaceParams(this.localization.teamEventCustomInvitedDesc, { eventName: item.teamEvent.eventCustomName, date: item.teamEvent.datetime.format('DD.MM'), time: item.teamEvent.datetime.format('HH:mm'), location: item.teamEvent.location }),
      route: item => this.getTeamEventRoute(item)
    },
    [NotificationType.tournament_invited]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentInvited,
        { teamName: item.team.name, tournamentName: item.tournament.name }
      ),
      description: () => this.localization.tournamentInvitedDesc,
      actions: [
        {
          text: this.localization.tournamentInviteDecline,
          action: item => this.declineTournamentInvite(item),
          buttonColor: 'default',
          buttonType: 'link',
          markViewed: true,
        },
        {
          text: this.localization.tournamentInviteAccept,
          action: item => this.acceptTournamentInvite(item),
          buttonColor: 'primary',
          buttonType: 'button',
        },
      ],
      externalLink: item => this.getTournamentRoute(item)
    },
    [NotificationType.tournament_joined]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentJoined,
        { tournamentName: item.tournament.name }
      ),
      description: () => this.localization.tournamentJoinedDesc,
      externalLink: item => this.getTournamentRoute(item)
    },
    [NotificationType.tournament_join_declined]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentJoinDeclined,
        { tournamentName: item.tournament.name }
      ),
      description: () => '',
      externalLink: item => this.getTournamentRoute(item)
    },
    [NotificationType.tournament_join_canceled]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentJoinCanceled,
        { tournamentName: item.tournament.name }
      ),
      description: () => '',
      externalLink: item => this.getTournamentRoute(item)
    },
    [NotificationType.tournament_user_invited]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentUserInvited,
        { initiator: item.initiator.shortName, tournamentName: item.tournament.name }
      ),
      description: () => this.localization.tournamentUserInvitedDesc,
      externalLink: item => this.getTournamentRoute(item)
    },
    [NotificationType.tournament_user_accepted_for_team]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentUserAcceptedForTeam,
        { userName: item.teamUser.user.shortName, tournamentName: item.tournament.name }
      ),
      description: (item: Notification) => '',
      route: item => this.getTournamentTeamApplicationRoute(item)
    },
    [NotificationType.tournament_user_accepted_for_user]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentUserAcceptedForUser,
        { tournamentName: item.tournament.name }
      ),
      description: (item: Notification) => this.localization.tournamentUserAcceptedForUserDesc,
      externalLink: item => this.getTournamentRoute(item)
    },
    [NotificationType.tournament_user_added]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentUserAdded,
        { tournamentName: item.tournament.name }
      ),
      description: (item: Notification) => this.localization.tournamentUserAddedDesc,
      externalLink: item => this.getTournamentRoute(item)
    },
    [NotificationType.tournament_user_declined_for_team]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentUserDeclinedForTeam,
        { userName: item.teamUser.user.shortName, tournamentName: item.tournament.name }
      ),
      description: () => '',
      route: item => this.getTournamentTeamApplicationRoute(item)
    },
    [NotificationType.tournament_user_declined_for_user]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentUserDeclinedForUser,
        { tournamentName: item.tournament.name }
      ),
      description: () => '',
      externalLink: item => this.getTournamentRoute(item)
    },
    [NotificationType.tournament_user_invite_deleted]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentUserInviteDeleted,
        { initiator: item.initiator.shortName, tournamentName: item.tournament.name }
      ),
      description: () => '',
      externalLink: item => this.getTournamentRoute(item)
    },
    [NotificationType.tournament_user_deleted]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentUserDeleted,
        { initiator: item.initiator.shortName, tournamentName: item.tournament.name }
      ),
      description: () => '',
      externalLink: item => this.getTournamentRoute(item)
    },
    [NotificationType.tournament_user_disqualified_for_team]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentUserDisqualifiedForTeam,
        { userName: item.teamUser.user.shortName, tournamentName: item.tournament.name }
      ),
      description: (item: Notification) =>
        replaceParams(
          this.localization.tournamentUserDisqualifiedForTeamDesc,
          { userName: item.teamUser.user.shortName }
        ) + ' ' + (
          item.data.matches_count
            ? `${item.data.matches_count} ${this.pluralize(item.data.matches_count, [this.localization.match1, this.localization.match2, this.localization.match3])}`
            : item.data.date_to
            ? replaceParams(this.localization.forPeriod, { start: moment(item.data.date_from).format('DD.MM.YYYY'), end: moment(item.data.date_to).format('DD.MM.YYYY')})
            : this.localization.forAllTime
        ),
      route: item => this.getTournamentTeamApplicationRoute(item)
    },
    [NotificationType.tournament_user_disqualified_for_user]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentUserDisqualifiedForUser,
        { tournamentName: item.tournament.name }
      ),
      description: (item: Notification) =>
        replaceParams(
          this.localization.tournamentUserDisqualifiedForUserDesc,
          { tournamentName: item.tournament.name }
        ) + ' ' + (
          item.data.matches_count
            ? `${item.data.matches_count} ${this.pluralize(item.data.matches_count, [this.localization.match1, this.localization.match2, this.localization.match3])}`
            : item.data.date_to
              ? replaceParams(this.localization.forPeriod, { start: moment(item.data.date_from).format('DD.MM.YYYY'), end: moment(item.data.date_to).format('DD.MM.YYYY')})
              : this.localization.forAllTime
        ),
      externalLink: item => this.getTournamentRoute(item)
    },
    [NotificationType.tournament_user_disqualification_updated_for_team]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentUserDisqualificationUpdatedForTeam,
        { userName: item.teamUser.user.shortName}
      ),
      description: (item: Notification) =>
        replaceParams(
          this.localization.tournamentUserDisqualificationUpdateForTeamDesc,
          { tournamentName: item.tournament.name, userName: item.teamUser.user.shortName }
        ) + ' ' + (
          item.data.matches_count
            ? `${item.data.matches_count} ${this.pluralize(item.data.matches_count, [this.localization.match1, this.localization.match2, this.localization.match3])}`
            : item.data.date_to
              ? replaceParams(this.localization.forPeriod, { start: moment(item.data.date_from).format('DD.MM.YYYY'), end: moment(item.data.date_to).format('DD.MM.YYYY')})
              : this.localization.untilEndTournament
        ),
      route: item => this.getTournamentTeamApplicationRoute(item)
    },
    [NotificationType.tournament_user_disqualification_updated_for_user]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => this.localization.tournamentUserDisqualificationUpdatedForUser,
      description: (item: Notification) =>
        replaceParams(
          this.localization.tournamentUserDisqualificationUpdatedForUserDesc,
          { tournamentName: item.tournament.name }
        ) + ' ' + (
          item.data.matches_count
            ? `${item.data.matches_count} ${this.pluralize(item.data.matches_count, [this.localization.match1, this.localization.match2, this.localization.match3])}`
            : item.data.date_to
              ? replaceParams(this.localization.forPeriod, { start: moment(item.data.date_from).format('DD.MM.YYYY'), end: moment(item.data.date_to).format('DD.MM.YYYY')})
              : this.localization.forAllTime
        ),
      externalLink: item => this.getTournamentRoute(item)
    },
    [NotificationType.tournament_team_deleted]: {
      logo: (item: Notification) => this.getTeamLogo(item),
      emptyLogoClass: (item: Notification) => this.getEmptyLogoClass(item),
      title: (item: Notification) => replaceParams(
        this.localization.tournamentTeamDeleted,
        { tournamentName: item.tournament.name }
      ),
      description: () => '',
      externalLink: item => this.getTournamentRoute(item)
    },
    [NotificationType.league_news]: {
      logo: (item: Notification) => this.getLeagueLogo(item),
      emptyLogoClass: (item: Notification) => item.league.getEmptyLogoClass(),
      title: (item: Notification) => replaceParams(
        this.localization.leagueNews,
        { leagueName: item.league.name }
      ),
      description: (item: Notification) => item.leagueNews.title,
      externalLink: item => this.getLeagueNewsLink(item),
      actions: [
        {
          text: this.localization.read,
          action: item => this.openLeagueNews(item),
          buttonColor: 'primary',
          buttonType: 'button',
          markViewed: true,
        }
      ]
    }
  };

  constructor(
    private teamService: TeamService,
    private userService: UserService,
    private teamEventService: TeamEventService,
    private notificationService: NotificationService,
    private router: Router,
    private localizationService: LocalizationService,
  ) {
    super();
  }

  acceptTeamInvite(item: Notification): Observable<any> {
    return this.userService.acceptInvite(item.teamInvite.id);
  }

  declineTeamInvite(item: Notification): Observable<any> {
    return this.userService.declineInvite(item.teamInvite.id);
  }

  acceptTeamJoinRequest(item: Notification): Observable<any> {
    return this.teamService.acceptInvite(item.teamInvite);
  }

  declineTeamJoinRequest(item: Notification): Observable<any> {
    return this.teamService.declineInvite(item.teamInvite);
  }

  acceptGameInvite(item: Notification): Observable<any> {
    return this.userService.acceptGameInvite(item.gameInvite.id);
  }

  declineGameInvite(item: Notification): Observable<any> {
    return this.userService.declineGameInvite(item.gameInvite.id);
  }

  acceptTeamEventInvite(item: Notification): Observable<any> {
    return this.teamEventService.acceptTeamEventInvite(item.teamEventInvite);
  }

  declineTeamEventInvite(item: Notification): Observable<any> {
    return this.teamEventService.declineTeamEventInvite(item.teamEventInvite);
  }

  acceptTournamentInvite(item: Notification): Observable<any> {
    return new Observable<any>(observer => {
      const link = [
      RoutingMap.ROUTE_TEAM_ROOT_ABSOLUTE,
      item.team.id.toString(),
      RoutingMap.ROUTE_TEAM_TOURNAMENT_INVITE,
      item.tournamentInvite.id.toString()
    ];
      this.router.navigate(link);
      observer.complete();
    });
  }

  declineTournamentInvite(item: Notification): Observable<any> {
    return this.teamService.declineTournamentInvite(item.tournamentInvite.id);
  }

  getTeamRoute(item: Notification): string[] {
    return [RoutingMap.ROUTE_TEAM_ROOT_ABSOLUTE, item.team.id.toString()];
  }

  getUserRoute(item: Notification): string[] {
    return [RoutingMap.ROUTE_PROFILE_ABSOLUTE, item.teamUser.id ? item.teamUser.user.id.toString() : item.teamInvite.user.id.toString()];
  }

  getGameRoute(item: Notification): string[] {
    return [
      RoutingMap.ROUTE_TEAM_ROOT_ABSOLUTE,
      item.team.id.toString(),
      RoutingMap.ROUTE_GAME_ROOT,
      item.game.id.toString()
    ];
  }

  getPollRoute(item: Notification): string[] {
    return [
      RoutingMap.ROUTE_TEAM_ROOT_ABSOLUTE,
      item.team.id.toString(),
      RoutingMap.ROUTE_TEAM_POLL_ROOT,
      item.poll.id.toString()
    ];
  }

  getTeamEventRoute(item: Notification): string[] {
    return [
      RoutingMap.ROUTE_TEAM_ROOT_ABSOLUTE,
      item.team.id.toString(),
      RoutingMap.ROUTE_TEAM_EVENT_ROOT,
      item.teamEvent.id.toString()
    ];
  }

  getTournamentRoute(item: Notification): string {
    return `https://${item.tournament.league.domain}/tournament/${item.tournament.alias}`;
  }

  getTournamentTeamApplicationRoute(item: Notification): string[] {
    if (!item.tournamentTeam.id) {
      return this.getTeamRoute(item);
    }
    return [
      RoutingMap.ROUTE_TEAM_ROOT_ABSOLUTE,
      item.team.id.toString(),
      RoutingMap.ROUTE_TEAM_TOURNAMENT_APPLICATION,
      item.tournamentTeam.id.toString()
    ];
  }

  getTeamLogo(item: Notification): string {
    return item.team.logo.path;
  }

  getEmptyLogoClass(item: Notification): string {
    return item.team.getEmptyLogoClass();
  }

  getLeagueLogo(item: Notification): string {
    return item.league.logo.path;
  }

  getLeagueNewsLink(item: Notification): string {
    return `https://${item.league.domain}/news/${item.leagueNews.id}`;
  }

  openLeagueNews(item: Notification): Observable<any> {
    window.open(this.getLeagueNewsLink(item), '_blank');
    return this.notificationService.markViewed(item.id, true);
  }

  getGameCompetitorTeamName(item: Notification): string {
    return item.team.id === item.game.team.id
      ? item.game.competitorTeam.name || item.game.competitorTeamName
      : item.game.team.name;
  }
}
